/**
 * Chat View - Vinnie Only
 *
 * Clean chat interface with organized message bubbles
 * and integrated quick action buttons.
 */

import { registerView } from '../viewRegistry';
import { buildApiUrl, getAuthWallet, getApiKey } from '../../utils/api';
import { trackEvent } from '../../utils/analytics';
import { setBadge } from '../installedApps';
import { FIRST_CHAT_STORY_LINE } from '../firstSession';
import { COPY } from '../../content/copy';

const VINNIE_NAME = 'VINNIE';
export const VINNIE_THREAD = 'vinnie';

/**
 * Characters the backend actually serves today. ADR-0021 forbids fake
 * presence: only Vinnie has real chat data until V2-L9 lands the
 * multi-character backend. Keep this list explicit so a recents row can never
 * be synthesized for a character with no data. Other threads appear only when
 * they already carry a real message.
 */
export const DATA_BACKED_THREADS = new Set([VINNIE_THREAD]);

// ADR-0021 presence timing. The indicator minimum is counted from the moment
// the player sent the message, so it never adds to real backend latency: a
// slow reply is shown the instant it arrives.
export const THINK_MS = 500;
export const TYPE_MS_PER_CHAR = 20;
export const TYPE_MAX_MS = 3500;
// ADR-0025 item 2: received text appears character by character.
export const REVEAL_CPS = 50;

const state = {
  messages: [],
  inputText: '',
  isTyping: false,
  scrollOffset: 0,
  maxScroll: 0,
  cursorVisible: true,
  lastCursorToggle: 0,
  inputActive: false,
  dynamicQuickActions: null, // Server-provided quick replies
  pending: 0, // sends still waiting for a reply
  typingSince: 0,
  activeThread: VINNIE_THREAD,
  unread: {}, // threadId -> count, for replies that landed while CHAT was closed
};

/** Typing-indicator minimum for a reply of `length` chars (ms). */
export function typingDuration(length) {
  return Math.min(TYPE_MAX_MS, THINK_MS + Math.max(0, length) * TYPE_MS_PER_CHAR);
}

function isChatOpen() {
  return typeof window !== 'undefined' && window.DCW_CURRENT_VIEW === 'comms_window';
}

function syncBadge() {
  setBadge('chat', Object.values(state.unread).reduce((a, b) => a + b, 0));
}

// Quick action definitions
const QUICK_ACTIONS = [
  { id: 'loan', label: 'LOAN', fullText: 'I need a loan' },
  { id: 'debt', label: 'DEBT', fullText: 'Check my debt' },
  { id: 'pay', label: 'PAY', fullText: 'I want to pay back' },
  { id: 'favor', label: 'FAVOR', fullText: 'Do me a favor' },
];

function addMessage(text, role = 'assistant', { silent = false, thread = VINNIE_THREAD } = {}) {
  const message = {
    id: Date.now(),
    text,
    role,
    thread,
    timestamp: Date.now(),
  };
  if (role === 'assistant' && !silent) {
    if (isChatOpen()) {
      message.revealStart = Date.now();
    } else {
      state.unread[thread] = (state.unread[thread] || 0) + 1;
      syncBadge();
    }
  }
  state.messages.push(message);
  // Auto scroll to bottom (scrollOffset = 0 means show bottom of content)
  setTimeout(() => {
    state.scrollOffset = 0;
  }, 10);
}

export async function sendMessage(text) {
  if (!text.trim()) return;

  // Player input is never delayed: finish any reply still being revealed.
  skipReveal();
  addMessage(text, 'user');
  trackEvent('comms_send', { channel: 'vinnie', length: text.length });
  state.inputText = '';
  const startedAt = Date.now();
  if (state.pending === 0) state.typingSince = startedAt;
  state.pending += 1;
  state.isTyping = true;

  try {
    const coreToken = localStorage.getItem('dcw_core_token');
    const token = coreToken || getApiKey();
    const wallet = getAuthWallet();
    const endpoint = coreToken ? '/api/v1/core/chat' : '/api/v1/chat';
    const response = await fetch(buildApiUrl(endpoint), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : '',
        'X-Wallet-Address': wallet || '',
      },
      body: JSON.stringify(
        coreToken
          ? {
              message: text,
              history: state.messages.slice(-8).map((message) => ({
                role: message.role,
                content: message.text,
              })),
            }
          : {
              message: text,
              character: 'vinnie',
              player_id: wallet || '',
            },
      ),
    });

    if (!response.ok) throw new Error('Chat failed');

    const data = await response.json();
    const reply = typeof data.response === 'string' ? data.response : '';
    if (!reply) throw new Error('Empty reply');

    // Keep the typing indicator up for the proportional minimum, measured
    // from the send: real latency already spent counts toward it.
    const remaining = startedAt + typingDuration(reply.length) - Date.now();
    if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));

    state.pending = Math.max(0, state.pending - 1);
    state.isTyping = state.pending > 0;
    addMessage(reply, 'assistant');

    // Update dynamic quick actions from server response
    if (data.quick_replies && Array.isArray(data.quick_replies)) {
      state.dynamicQuickActions = data.quick_replies.map(qr => ({
        id: qr.payload || qr.title,
        label: (qr.title || qr.payload).toUpperCase().substring(0, 10),
        fullText: qr.payload || qr.title
      }));
    } else {
      state.dynamicQuickActions = null; // Reset to use defaults
    }
  } catch (err) {
    state.pending = Math.max(0, state.pending - 1);
    state.isTyping = state.pending > 0;
    addMessage(COPY.core.offline, 'system');
    state.dynamicQuickActions = null;
  }
}

// ============================================================
// RECEIVED-TEXT REVEAL (ADR-0025 item 2)
// ============================================================

/** Characters of `message` visible at time `t` (full text when not revealing). */
export function visibleChars(message, t = Date.now()) {
  if (!message.revealStart) return message.text.length;
  const n = Math.floor(((t - message.revealStart) / 1000) * REVEAL_CPS) + 1;
  return Math.max(0, Math.min(message.text.length, n));
}

export function isRevealing(t = Date.now()) {
  return state.messages.some((m) => m.revealStart && visibleChars(m, t) < m.text.length);
}

/** Show every received message in full. Returns true if anything was hidden. */
export function skipReveal() {
  let skipped = false;
  const t = Date.now();
  state.messages.forEach((m) => {
    if (m.revealStart) {
      if (visibleChars(m, t) < m.text.length) skipped = true;
      m.revealStart = null;
    }
  });
  return skipped;
}

// ============================================================
// THREADS (ADR-0021: one Chat app, recents for the cast)
// ============================================================

/**
 * Recents are derived from real data only: Vinnie always (he is the one
 * character the backend serves today), plus any other thread that has
 * messages. No placeholder characters.
 */
export function getThreads() {
  const ids = [...DATA_BACKED_THREADS];
  state.messages.forEach((m) => {
    if (m.thread && !ids.includes(m.thread)) ids.push(m.thread);
  });
  return ids.map((id) => {
    const messages = state.messages.filter((m) => (m.thread || VINNIE_THREAD) === id);
    return {
      id,
      name: id === VINNIE_THREAD ? VINNIE_NAME : id.toUpperCase(),
      unread: state.unread[id] || 0,
      typing: id === VINNIE_THREAD && state.isTyping,
      last: messages[messages.length - 1] || null,
    };
  });
}

export function getThreadMessages(id = state.activeThread) {
  return state.messages.filter((m) => (m.thread || VINNIE_THREAD) === id);
}

export function markThreadRead(id = state.activeThread) {
  if (state.unread[id]) {
    delete state.unread[id];
    syncBadge();
  }
}

// ============================================================
// KEYBOARD HANDLER - EXPORTED FOR DopeOS
// ============================================================

export function handleChatKey(key) {
  // DopeOS registers one capture listener, so no duplicate-key debounce is
  // needed here. A debounce dropped fast repeated characters (e.g. "hello").
  // Only process if input is active
  if (key === 'PageUp' || key === 'PageDown') {
    const step = key === 'PageUp' ? 120 : -120;
    state.scrollOffset = Math.max(0, Math.min(state.scrollOffset + step, state.maxScroll));
    return;
  }
  if (key === 'Enter' && !state.inputText.length) {
    skipReveal(); // Enter on an empty input fast-forwards Vinnie's text
    return;
  }
  if (!state.inputActive) {
    // Auto-activate on first keypress if it's a printable character
    if (key.length === 1 && key.charCodeAt(0) >= 32 && key.charCodeAt(0) < 127) {
      state.inputActive = true;
      state.inputText += key;
    }
    return;
  }

  if (key === 'Enter') {
    if (state.inputText.length > 0) {
      sendMessage(state.inputText);
    }
  } else if (key === 'Backspace') {
    state.inputText = state.inputText.slice(0, -1);
  } else if (key === 'Escape') {
    state.inputActive = false;
    state.inputText = '';
  } else if (key.length === 1 && key.charCodeAt(0) >= 32 && key.charCodeAt(0) < 127) {
    // Printable ASCII only, limit length
    if (state.inputText.length < 150) {
      state.inputText += key;
    }
  }
}

// ============================================================
// EVENT LISTENERS
// ============================================================

// Mouse wheel for scrolling
if (typeof window !== 'undefined') {
  window.addEventListener('wheel', (e) => {
    if (window.DCW_CURRENT_VIEW === 'chat' || window.DCW_CURRENT_VIEW === 'comms_window') {
      // scrollOffset is measured up from the newest message.
      state.scrollOffset -= e.deltaY * 0.5;
      state.scrollOffset = Math.max(0, Math.min(state.scrollOffset, state.maxScroll));
      e.preventDefault();
    }
  }, { passive: false });
}

// ============================================================
// INITIALIZATION
// ============================================================

export function addAssistantMessage(text) {
  // The first-session bridge can race the legacy intro seed; keep one copy.
  if (state.messages.some((message) => message.text === text)) return false;
  addMessage(text, 'assistant');
  return true;
}

export function seedChatIntro() {
  addMessage(FIRST_CHAT_STORY_LINE, 'assistant', { silent: true });
}

export function getChatState() {
  return state;
}

export function getChatMessages() {
  return state.messages.map((message) => ({ ...message }));
}

export function resetChatState() {
  state.messages = [];
  state.inputText = '';
  state.scrollOffset = 0;
  state.inputActive = false;
  state.pending = 0;
  state.isTyping = false;
  state.unread = {};
  syncBadge();
}

export default {
  handleChatKey,
  skipReveal,
  getThreads,
  addMessage,
  resetChatState,
  getChatState,
  getChatMessages,
  sendMessage,
};
